import { z } from 'zod'

import { BookingStatus } from '../constants/booking'
import {
  MembershipLevel,
  MembershipLevelValue,
  MembershipStatus,
} from '../constants/membership'
import { dbEntitySchema, listQueryZodFields } from './system'
import { customerSchema } from './customer'
import { UserFavoriteType } from '../constants/user'
import { referralSchema } from './referral'

export const favoriteSchema = z.object({
  _id: z.string(),
  type: z.nativeEnum(UserFavoriteType),
  venue: z.string().nullish(),
  event: z.string().nullish(),
})

export const userSchema = dbEntitySchema.extend({
  admin: z.boolean().nullish(),
  name: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().email().nullish(),
  phone: z.string().nullish(),
  imageUrl: z.string().nullish(),
  birthday: z.date().nullish(),
  gender: z.string().nullish(),
  prospect: z.string().nullish(),
  password: z.string().nullish(),
  marketingOptIn: z.boolean().nullish(),
  marketingSmsOptIn: z.boolean().nullish(),
  marketingSmsOptInAt: z.date().nullish(),
  marketingOptInSignature: z.string().nullish(),
  lastContacted: z.date().nullish(),
  membershipStatus: z.nativeEnum(MembershipStatus).nullish(),
  membershipLevel: z
    .number()
    .min(MembershipLevelValue.EVERYONE)
    .max(MembershipLevelValue.ADMIN)
    .nullish(),
  resetToken: z.string().nullish(),
  resetCode: z.string().nullish(),
  resetCodeExpires: z.date().nullish(),
  resetCodeAttempts: z.number().nullish(),
  loginCode: z.string().nullish(),
  loginCodeExpires: z.date().nullish(),
  loginCodeAttempts: z.number().nullish(),
  verificationChannel: z.enum(['email']).nullish(),
  verificationCode: z.string().nullish(),
  verificationCodeExpires: z.date().nullish(),
  verificationCodeAttempts: z.number().nullish(),
  emailVerifiedAt: z.date().nullish(),
  phoneVerifiedAt: z.date().nullish(),
  phoneVerificationCode: z.string().nullish(),
  phoneVerificationCodeExpires: z.date().nullish(),
  phoneVerificationCodeAttempts: z.number().nullish(),
  tokenVersion: z.number().nullish(),
  referral: referralSchema.partial().optional(),
  referralCode: z.string().nullish(),
  credit: z.number().nullish(),
  points: z.number().nullish(),
  administrativeArea: z.string().nullish(),
  preferences: z
    .object({
      favorites: z.array(favoriteSchema).nullish(),
    })
    .nullish(),
  bookingCount: z.number().nullish(),
  google: z
    .object({
      id: z.string().nullish(),
      email: z.string().email().nullish(),
      avatar: z.string().nullish(),
      url: z.string().nullish(),
      cover: z.string().nullish(),
    })
    .nullish(),
})

export const userStatsSchema = dbEntitySchema.extend({
  user: z.string(),
  current: z.boolean(),
  date: z.date(),
  client: z.string(),
  reservationDate: z.date(),
  city: z.string(),
  venue: z.string(),
  event: z.string(),
  booking: z.string(),
  tableTransactions: z.number(),
  tableRevenue: z.number(),
  totalTableTransactions: z.number(),
  totalTableRevenue: z.number(),
  ticketTransactions: z.number(),
  ticketRevenue: z.number(),
  totalTicketTransactions: z.number(),
  totalTicketRevenue: z.number(),
  transactions: z.number(),
  revenue: z.number(),
  totalTransactions: z.number(),
  totalRevenue: z.number(),
  creditUsed: z.number(),
  creditTransactions: z.number(),
  totalCreditUsed: z.number(),
  totalCreditTransactions: z.number(),
})

export const userCreateBodySchema = userSchema.omit({
  id: true,
  resetToken: true,
  resetCode: true,
  resetCodeExpires: true,
  resetCodeAttempts: true,
  loginCode: true,
  loginCodeExpires: true,
  loginCodeAttempts: true,
})

//input schemas
export const usersListQuerySchema = z.object({
  ...listQueryZodFields,
  search: z.string().optional().nullable(),
})
export const usersCustomersListQuerySchema = z.object({
  ...listQueryZodFields,
  search: z.string().optional().nullable(),
})

/** Admin/API responses: never expose password or resetToken. */
export const adminUserViewSchema = userSchema
  .omit({
    password: true,
    resetToken: true,
    resetCode: true,
    resetCodeExpires: true,
    resetCodeAttempts: true,
    loginCode: true,
    loginCodeExpires: true,
    loginCodeAttempts: true,
    verificationCode: true,
    verificationCodeExpires: true,
    verificationCodeAttempts: true,
    phoneVerificationCode: true,
    phoneVerificationCodeExpires: true,
    phoneVerificationCodeAttempts: true,
  })
  .extend({
    email: z.union([z.string().email(), z.literal('')]).nullish(),
    created: z.coerce.date(),
    modified: z.coerce.date(),
    birthday: z.coerce.date().nullish(),
    lastContacted: z.coerce.date().nullish(),
  })

//response schemas
export const usersListResponseSchema = z.object({
  data: z.array(
    adminUserViewSchema.extend({
      hasPushDevice: z.boolean(),
    })
  ),
})

export const userAdminGetResponseSchema = z.object({
  data: adminUserViewSchema,
})

export const userAdminPatchBodySchema = z
  .object({
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    name: z.string().nullish(),
    email: z.union([z.string().email(), z.literal('')]).nullish(),
    phone: z.string().nullish(),
    imageUrl: z.string().nullish(),
    gender: z.string().nullish(),
    marketingOptIn: z.boolean().nullish(),
    marketingSmsOptIn: z.boolean().nullish(),
    birthday: z.coerce.date().nullish(),
  })
  .strict()

export const userAdminPatchResponseSchema = userAdminGetResponseSchema

export const consumerUserBookingRowSchema = z.object({
  id: z.string(),
  date: z.coerce.date(),
  status: z.nativeEnum(BookingStatus),
  total: z.number().nullish(),
  venueName: z.string().nullish(),
  name: z.string().nullish(),
})

export const consumerUserBookingsListQuerySchema = z.object({
  ...listQueryZodFields,
})

export const consumerUserBookingsListResponseSchema = z.object({
  data: z.array(consumerUserBookingRowSchema),
})

export const userPasswordResetEmailResponseSchema = z.object({
  data: z.object({ sent: z.literal(true) }),
})

export const usersCustomersListResponseSchema = z.object({
  data: z.array(customerSchema),
})

/** Safe subset for `GET /v1/users/me` (no password, resetToken, etc.). Dates coerced for JSON/Mongoose. */
export const consumerCurrentUserSchema = userSchema
  .omit({
    password: true,
    resetToken: true,
    resetCode: true,
    resetCodeExpires: true,
    resetCodeAttempts: true,
    loginCode: true,
    loginCodeExpires: true,
    loginCodeAttempts: true,
    verificationCode: true,
    verificationCodeExpires: true,
    verificationCodeAttempts: true,
    phoneVerificationCode: true,
    phoneVerificationCodeExpires: true,
    phoneVerificationCodeAttempts: true,
  })
  .extend({
    created: z.coerce.date(),
    modified: z.coerce.date(),
    hasPassword: z.boolean().nullish(),
    googleEmail: z.string().email().nullish(),
    hasGoogleEmailMismatch: z.boolean().nullish(),
    birthday: z.coerce.date().nullish(),
    lastContacted: z.coerce.date().nullish(),
    marketingSmsOptInAt: z.coerce.date().nullish(),
    emailVerifiedAt: z.coerce.date().nullish(),
    phoneVerifiedAt: z.coerce.date().nullish(),
    location: z
      .object({
        preferred: z.string().nullish(),
      })
      .nullish(),
  })
